import './CompanyFormModal.css';

import { useState, useEffect } from 'react';
import { Modal, Form } from 'react-bootstrap';
import {
    GET_ACCOUNT_BY_NAME_ADDRESS,
    CREATE_ACCOUNT,
    UPDATE_ACCOUNT,
    CREATE_ACCOUNT_PROPERTY,
    useLazyQuery,
    useMutation,
} from 'utils/graphql';
import SearchResultsList from 'components/SearchResultsList/SearchResultsList';
import LoadingButton from 'components/LoadingButton/LoadingButton';
import { v4 as uuidv4 } from 'uuid';
import { formatUTCDateTime } from 'utils/date.util';
import { useLocalStorage } from 'utils/localstorage.util';
import { PROPERTY_NAMESPACE } from 'App';
import { useTranslation } from 'react-i18next';

const CompanyFormModal = ({ accountToEdit = null, isOpen, setIsOpen, onSuccess }) => {
    const { t } = useTranslation();
    const [companyName, setCompanyName] = useState('');
    const [companyNameError, setCompanyNameError] = useState('');
    const [companyAddress, setCompanyAddress] = useState(null);
    const [addressSearchTerm, setAddressSearchTerm] = useState('');
    const [addressError, setAddressError] = useState('');
    const [companyError, setCompanyError] = useState('');
    const [addressSearchResults, setAddressSearchResults] = useState([]);
    const [isLoadingSearchResults, setIsLoadingSearchResults] = useState(false);
    const [isLoadingAddress, setIsLoadingAddress] = useState(false);
    const [showAddressResults, setShowAddressResults] = useState(false);
    const [performAddressSearch, setPerformAddressSearch] = useState(true);
    const [validated, setValidated] = useState(false);
    const [isLoading, setIsLoading] = useState(false);
    const [property] = useLocalStorage(PROPERTY_NAMESPACE);
    const isEditing = accountToEdit != null;

    const [getAccountsByNameAddress] = useLazyQuery(GET_ACCOUNT_BY_NAME_ADDRESS);

    const [createAccount] = useMutation(CREATE_ACCOUNT, {
        variables: {
            external_id__c: uuidv4(),
            sfid: uuidv4(),
        },
    });

    const [createAccountProperty] = useMutation(CREATE_ACCOUNT_PROPERTY, {
        variables: {
            sfid: uuidv4(),
            property__c: property.sfid,
        },
    });

    const [updateAccount] = useMutation(UPDATE_ACCOUNT);

    const resetForm = () => {
        setCompanyName('');
        setCompanyNameError('');
        setCompanyAddress(null);
        setAddressSearchTerm('');
        setAddressError('');
        setCompanyError('');
        setAddressSearchResults([]);
        setIsLoadingSearchResults(false);
        setIsLoadingAddress(false);
        setShowAddressResults(false);
        setPerformAddressSearch(true);
        setValidated(false);
        setIsLoading(false);
    };

    const createCompany = async () => {
        setIsLoading(true);
        if (await validate()) {
            const createddate = formatUTCDateTime(new Date());
            const { data: createAccountData, error: createAccountError } = await createAccount({
                variables: {
                    name: companyName,
                    ...companyAddress,
                    createddate,
                },
            });

            if (!createAccountError) {
                const account = createAccountData.createRow.account;
                // we don't need to wait for the response
                createAccountProperty({
                    variables: {
                        account__c: account.sfid,
                        createddate,
                    },
                });
                setIsOpen(false);
                onSuccess(account);
            } else {
                console.log('error', createAccountError);
                setCompanyError('company_creation_fail');
                setIsLoading(false);
            }
        } else {
            setIsLoading(false);
        }
    };

    const validate = async () => {
        setCompanyNameError('');
        setAddressError('');
        setCompanyError('');
        let errors = false;
        if (!companyName) {
            setCompanyNameError('company_name_empty_error');
            errors = true;
        } else if (companyName.match(/ivanhoe cambridge/i)) {
            setCompanyNameError('company_name_ivanhoe_error');
            errors = true;
        }
        if (!addressSearchTerm) {
            setAddressError('address_empty_error');
            errors = true;
        } else if (!companyAddress) {
            setAddressError('valid_address_error');
            errors = true;
        }

        if (errors) {
            setValidated(true);
            return false;
        } else if (
            !isEditing ||
            accountToEdit.name !== companyName ||
            accountToEdit.address_line_1__c !== companyAddress.address_line_1__c
        ) {
            const { data } = await getAccountsByNameAddress({
                variables: {
                    name: companyName,
                    address: companyAddress.address_line_1__c,
                },
                fetchPolicy: 'network-only',
            });
            if (data.table.count > 0) {
                setCompanyError('company_exists_error');
                setValidated(true);
                return false;
            }
            return true;
        }
    };

    const editCompany = async () => {
        setIsLoading(true);
        if (await validate()) {
            updateAccount({
                variables: {
                    id: accountToEdit.id,
                    name: companyName,
                    ...companyAddress,
                    external_id__c: accountToEdit.external_id__c,
                },
            })
                .then((response) => {
                    if (response.errors) {
                        setCompanyError('company_update_fail');
                        setIsLoading(false);
                    } else {
                        setIsOpen(false);
                        onSuccess(response.data.updateRow.account);
                    }
                })
                .catch((error) => {
                    setIsLoading(false);
                    console.log('error', error);
                });
        } else {
            setIsLoading(false);
        }
    };

    const onAddressSelect = (address) => {
        setShowAddressResults(false);
        setPerformAddressSearch(false);
        setAddressSearchTerm(`${address.Text}, ${address.Description}`);
        setIsLoadingAddress(true);
        fetch(
            `https://ws1.postescanada-canadapost.ca/AddressComplete/Interactive/Retrieve/v2.10/json3.ws?Key=${
                process.env.REACT_APP_CANADA_POST_API_KEY
            }&Id=${encodeURIComponent(address.Id)}`
        )
            .then((response) => response.json())
            .then((data) => data.Items)
            .then((items) => {
                // const address = items.find((item) => item.Language === 'ENG');
                const address = items[0];
                if (address) {
                    setCompanyAddress({
                        address_line_1__c: `${address.Line1}, ${address.City}, ${address.Province}, ${address.PostalCode}, ${address.CountryName}`,
                        billingstreet: address.Line1,
                        billingcity: address.City,
                        billingstate: address.Province,
                        billingpostalcode: address.PostalCode,
                        billingcountry: address.CountryName,
                    });
                }
                setIsLoadingAddress(false);
            });
    };

    const onAddressSearch = () => {
        if (!addressSearchTerm) return;
        if (companyAddress) setCompanyAddress(null);
        setIsLoadingSearchResults(true);
        setShowAddressResults(true);

        canadaPostAddressSearch(addressSearchTerm).then((results) => {
            setAddressSearchResults(results);
            setIsLoadingSearchResults(false);
        });
    };

    const canadaPostAddressSearch = async (searchTerm) => {
        const addressCompleteFindUrl = new URL(
            'https://ws1.postescanada-canadapost.ca/AddressComplete/Interactive/Find/v2.10/json3.ws'
        );
        addressCompleteFindUrl.searchParams.append(
            'Key',
            process.env.REACT_APP_CANADA_POST_API_KEY
        );
        addressCompleteFindUrl.searchParams.append('SearchTerm', searchTerm);
        addressCompleteFindUrl.searchParams.append('Country', 'CAN');
        addressCompleteFindUrl.searchParams.append('MaxResults', 10);

        const initialResponse = await fetch(addressCompleteFindUrl.href);
        const initialData = await initialResponse.json();

        if (
            initialData.Items &&
            initialData.Items.length > 0 &&
            initialData.Items[0].Next === 'Find'
        ) {
            // Use the Find endpoint again with LastId to get sub-results
            const findSubResultsUrl = new URL(
                'https://ws1.postescanada-canadapost.ca/AddressComplete/Interactive/Find/v2.10/json3.ws'
            );
            findSubResultsUrl.searchParams.append('Key', process.env.REACT_APP_CANADA_POST_API_KEY);
            findSubResultsUrl.searchParams.append('LastId', initialData.Items[0].Id);

            const subResultsResponse = await fetch(findSubResultsUrl.href);
            const subResultsData = await subResultsResponse.json();

            return subResultsData.Items;
        } else {
            return initialData.Items;
        }
    };

    useEffect(() => {
        if (validated) setValidated(false);
    }, [addressSearchTerm, companyName]);

    useEffect(() => {
        const timer = setTimeout(() => {
            performAddressSearch ? onAddressSearch() : setPerformAddressSearch(true);
        }, 500);
        return () => {
            clearTimeout(timer);
        };
    }, [addressSearchTerm]);

    useEffect(() => {
        if (!isOpen) return;

        resetForm();
        if (isEditing) {
            setPerformAddressSearch(false);
            setCompanyName(accountToEdit.name);
            setAddressSearchTerm(accountToEdit.address_line_1__c);
            setCompanyAddress({
                address_line_1__c: accountToEdit.address_line_1__c,
                billingstreet: accountToEdit.billingstreet,
                billingcity: accountToEdit.billingcity,
                billingstate: accountToEdit.billingstate,
                billingpostalcode: accountToEdit.billingpostalcode,
                billingcountry: accountToEdit.billingcountry,
            });
            canadaPostAddressSearch(accountToEdit.address_line_1__c).then((results) => {
                setAddressSearchResults(results);
            });
        }
    }, [isOpen]);

    return (
        <Modal show={isOpen} onHide={() => setIsOpen(false)}>
            <Modal.Header closeButton>
                <Modal.Title>
                    <h5>{t(isEditing ? 'edit_company' : 'create_company')}</h5>
                </Modal.Title>
            </Modal.Header>
            <Modal.Body>
                <Form>
                    <Form.Group className="mb-3">
                        <Form.Label>
                            <span className="required-field">*</span> {t('company_name')}
                        </Form.Label>
                        <Form.Control
                            isInvalid={validated && companyNameError}
                            type="text"
                            value={companyName}
                            onChange={(event) => setCompanyName(event.target.value)}
                        />
                        {companyNameError && (
                            <Form.Control.Feedback type="invalid">
                                {t(companyNameError)}
                            </Form.Control.Feedback>
                        )}
                    </Form.Group>
                    <div className="position-relative">
                        <Form.Group className="mb-3">
                            <Form.Label>
                                <span className="required-field">*</span> {t('address')}
                            </Form.Label>
                            <Form.Control
                                isInvalid={validated && addressError}
                                type="text"
                                name="cmp-street"
                                id="cmp-street"
                                value={addressSearchTerm}
                                onChange={(event) => setAddressSearchTerm(event.target.value)}
                                onClick={() =>
                                    addressSearchTerm &&
                                    !showAddressResults &&
                                    setShowAddressResults(true)
                                }
                            />
                            {addressError && (
                                <Form.Control.Feedback type="invalid">
                                    {t(addressError)}
                                </Form.Control.Feedback>
                            )}
                        </Form.Group>
                        <SearchResultsList
                            show={showAddressResults}
                            onClickOutside={() => setShowAddressResults(false)}
                            onClickCallback={(result) => onAddressSelect(result)}
                            searchResults={addressSearchResults}
                            isLoadingResults={isLoadingSearchResults}
                            getText={(result) => `${result.Text}, ${result.Description}`}
                            isTextBold={false}
                            topPosition={'75px'}
                        />
                    </div>
                </Form>
                {validated && companyError && (
                    <div
                        className="invalid-feedback fw-bold"
                        style={{ display: companyError ? 'block' : 'none' }}
                    >
                        {t(companyError)}
                    </div>
                )}
            </Modal.Body>
            <Modal.Footer>
                <LoadingButton
                    onClick={isEditing ? editCompany : createCompany}
                    isLoading={isLoading}
                    isDisabled={isLoadingAddress}
                >
                    {t(isEditing ? 'edit' : 'create')}
                </LoadingButton>
            </Modal.Footer>
        </Modal>
    );
};

export default CompanyFormModal;
